// Vídeos curtos da página de produto (snippets/short-videos.liquid), adaptado do componente do
// Perfumistta:
// - círculos com prévia do vídeo tocando sem som (menor MP4), só com o bloco na tela e depois que
//   a página termina de carregar, para não disputar banda com a foto principal (LCP)
// - modal em tela cheia com rolagem vertical entre os vídeos (como stories/reels), barra de
//   progresso arrastável, pausa no toque, setas e teclado; sempre sem som
// - vídeos fora de vista são descarregados da memória
//
// O snippet pode aparecer duas vezes na página (seção no topo para o celular e bloco ao lado da
// foto para o desktop); a instância escondida nunca entra na tela e não carrega nada.
if (!customElements.get('short-videos-component')) {
  customElements.define('short-videos-component', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.items = Array.from(this.querySelectorAll('.short-video-item'));
      this.modal = this.querySelector('.short-video-modal');
      this.isVisible = false;
      this.progressFrame = null;
      // Sem prévia animada para quem pede menos movimento ou economia de dados
      this.previewsAllowed = !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        !(navigator.connection && navigator.connection.saveData);
      this.pageLoaded = document.readyState === 'complete';
      if (!this.pageLoaded) {
        window.addEventListener('load', () => {
          this.pageLoaded = true;
          this.managePreviews();
        }, { once: true });
      }

      this.setupPreviews();
      if (this.modal) this.setupModal();
    }

    /* Carregamento ------------------------------------------------------- */

    loadVideo(video) {
      if (!video || video.getAttribute('src') || !video.dataset.src) return;
      if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
      video.preload = 'auto';
      video.src = video.dataset.src;
      video.load();
    }

    // Remove a fonte e libera buffer/decodificador; o poster volta a aparecer
    unloadVideo(video) {
      if (!video || !video.getAttribute('src')) return;
      video.pause();
      video.removeAttribute('src');
      video.preload = 'none';
      video.load();
    }

    // Executa só depois que o vídeo começou a tocar, para não disputar banda com ele
    afterPlaying(video, callback) {
      if (!video.paused && video.readyState >= 3) {
        callback();
        return;
      }
      video.addEventListener('playing', callback, { once: true });
    }

    /* Prévias nos círculos ----------------------------------------------- */

    setupPreviews() {
      this.items.forEach((item) => {
        const video = item.querySelector('.short-video-preview');
        if (!video) return;
        video.addEventListener('playing', () => item.classList.add('is-playing'));
        item.addEventListener('click', () => {
          // data-index liga o círculo ao slide do mesmo vídeo
          const slide = this.slides && this.slides.findIndex((s) => s.dataset.index === item.dataset.index);
          if (slide >= 0) this.openModal(slide);
        });
      });

      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          this.managePreviews();
        });
      }, { threshold: 0.2 }).observe(this);

      document.addEventListener('visibilitychange', () => this.managePreviews());
    }

    managePreviews() {
      const previews = this.querySelectorAll('.short-video-preview');
      const canPlay = this.previewsAllowed && this.pageLoaded && this.isVisible && !document.hidden &&
        !(this.modal && this.modal.classList.contains('is-open'));

      previews.forEach((video) => {
        if (!canPlay) {
          video.pause();
          return;
        }
        this.loadVideo(video);
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    /* Modal --------------------------------------------------------------- */

    setupModal() {
      // Fora do bloco do produto: position: fixed sem ser afetado por transform/overflow dos pais
      document.body.appendChild(this.modal);
      this.slides = Array.from(this.modal.querySelectorAll('.short-video-slide'));
      this.viewport = this.modal.querySelector('.short-video-modal-viewport');
      this.nextHint = this.modal.querySelector('.short-video-next-hint');
      this.prevHint = this.modal.querySelector('.short-video-prev-hint');
      this.closeButton = this.modal.querySelector('.short-video-modal-close');

      this.closeButton.addEventListener('click', () => this.closeModal());
      this.nextHint.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
      this.prevHint.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));

      document.addEventListener('keydown', (e) => {
        if (!this.modal.classList.contains('is-open')) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.goToSlide(this.currentIndex + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.goToSlide(this.currentIndex - 1);
        } else if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      this.slides.forEach((slide) => this.setupSlideVideo(slide.querySelector('video'), slide));
      this.setupProgressSeek();
      this.setupPlayToggle();
      this.modal.addEventListener('click', (e) => {
        if (e.target.closest('.short-video-product__buy')) this.buy();
      });

      // O vídeo que ocupa a tela toca; os outros pausam
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.handleSlideChange(this.slides.indexOf(entry.target));
          else entry.target.querySelector('video').pause();
        });
      }, { threshold: 0.6, root: this.viewport });
      this.slides.forEach((slide) => observer.observe(slide));
    }

    setupSlideVideo(video, slide) {
      const loader = slide.querySelector('.short-video-loading');
      const showLoader = () => { if (!video.paused) loader.classList.add('is-visible'); };
      const hideLoader = () => loader.classList.remove('is-visible');
      // Carregando só quando o vídeo deveria estar tocando e está esperando dados
      video.addEventListener('waiting', showLoader);
      video.addEventListener('stalled', showLoader);
      ['playing', 'canplay', 'timeupdate', 'seeked', 'pause'].forEach((type) => video.addEventListener(type, hideLoader));

      video.addEventListener('pause', () => { if (!this.isScrubbing) slide.classList.add('is-paused'); });
      video.addEventListener('play', () => slide.classList.remove('is-paused'));
    }

    setupProgressSeek() {
      this.isScrubbing = false;
      let container = null;
      let video = null;
      let wasPlaying = false;

      const seek = (clientX) => {
        if (!video || !video.duration) return;
        const rect = container.getBoundingClientRect();
        const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
        video.currentTime = ratio * video.duration;
        this.renderProgress(container, ratio);
      };

      this.modal.addEventListener('pointerdown', (e) => {
        const target = e.target.closest('.short-video-progress');
        if (!target) return;
        e.preventDefault();
        this.isScrubbing = true;
        container = target;
        video = container.closest('.short-video-slide').querySelector('video');
        wasPlaying = !video.paused;
        video.pause();
        container.classList.add('is-scrubbing');
        if (container.setPointerCapture) container.setPointerCapture(e.pointerId);
        seek(e.clientX);
      });

      this.modal.addEventListener('pointermove', (e) => {
        if (this.isScrubbing && container) seek(e.clientX);
      });

      const end = () => {
        if (!this.isScrubbing) return;
        this.isScrubbing = false;
        container.classList.remove('is-scrubbing');
        if (wasPlaying) video.play().catch(() => {});
        container = null;
        video = null;
      };
      this.modal.addEventListener('pointerup', end);
      this.modal.addEventListener('pointercancel', end);
    }

    renderProgress(container, ratio) {
      container.querySelector('.short-video-progress__bar').style.transform = 'scaleX(' + ratio + ')';
      container.querySelector('.short-video-progress__thumb').style.left = (ratio * 100) + '%';
    }

    // Toque/clique no vídeo pausa e retoma
    setupPlayToggle() {
      this.modal.addEventListener('click', (e) => {
        if (e.target.closest('button, .short-video-progress, .short-video-product')) return;
        const slide = e.target.closest('.short-video-slide');
        if (!slide) return;
        const video = slide.querySelector('video');
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      });
    }

    // Fecha o vídeo e usa o botão de compra da página. Faltando escolher o tamanho, leva até as
    // opções e o clique mostra o aviso do tema (assets/variant-unselect.js)
    buy() {
      const visible = (el) => el.getClientRects().length > 0;
      const atc = Array.from(document.querySelectorAll('.product-form__actions button[name="add"]')).find(visible);
      this.closeModal();
      if (!atc) return;
      if (atc.dataset.atcPending === 'true') {
        const picker = Array.from(document.querySelectorAll('.sf-prod__block variant-picker, .product__variant-picker')).find(visible);
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      atc.click();
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.viewport.scrollTo({ top: this.slides[index].offsetTop, behavior: 'smooth' });
    }

    openModal(index) {
      this.lastFocus = document.activeElement;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      this.classList.add('is-modal-open');
      this.managePreviews();
      document.documentElement.classList.add('short-videos-open');
      this.viewport.scrollTo({ top: this.slides[index].offsetTop, behavior: 'instant' });
      this.handleSlideChange(index);
      this.closeButton.focus({ preventScroll: true });
    }

    closeModal() {
      cancelAnimationFrame(this.progressFrame);
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('short-videos-open');
      // Para e descarrega os vídeos do modal (voltam a carregar ao reabrir)
      this.modal.querySelectorAll('video').forEach((video) => this.unloadVideo(video));
      this.classList.remove('is-modal-open');
      this.managePreviews();
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    handleSlideChange(index) {
      if (!this.modal.classList.contains('is-open')) return;
      this.currentIndex = index;
      const isLast = index >= this.slides.length - 1;
      // No último vídeo troca a seta de "próximo" (embaixo) pela de "voltar" (em cima)
      this.nextHint.classList.toggle('is-hidden', isLast);
      this.prevHint.classList.toggle('is-hidden', !isLast || this.slides.length < 2);
      cancelAnimationFrame(this.progressFrame);

      // Pausa os outros e descarrega os que não são vizinhos
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        const other = slide.querySelector('video');
        other.pause();
        if (Math.abs(i - index) > 1) this.unloadVideo(other);
      });

      const slide = this.slides[index];
      const video = slide.querySelector('video');
      const progress = slide.querySelector('.short-video-progress');
      this.loadVideo(video);
      this.afterPlaying(video, () => {
        if (this.slides[index + 1]) this.loadVideo(this.slides[index + 1].querySelector('video'));
        if (this.slides[index - 1]) this.loadVideo(this.slides[index - 1].querySelector('video'));
      });
      video.muted = true;

      const update = () => {
        if (video.duration && !this.isScrubbing) this.renderProgress(progress, video.currentTime / video.duration);
        this.progressFrame = requestAnimationFrame(update);
      };
      this.progressFrame = requestAnimationFrame(update);

      video.play().catch(() => {});
    }
  });
}
